import React, { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";

const StyledFitText = styled.div`
    position: relative;
`;
const FitTextRenderArea = styled.div`
    position: absolute;
    white-space: nowrap;
`;

export interface FitTextProps extends React.HTMLAttributes<HTMLDivElement> {
    text: string;
    constrainWidth?: boolean;
    constrainHeight?: boolean;
}
export const FitText = (props: FitTextProps) => {
    const { text, constrainWidth = true, constrainHeight = false, ...restProps } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const renderAreaRef = useRef<HTMLDivElement>(null);

    const sizeText = useCallback(() => {
        if (!containerRef.current || !renderAreaRef.current) return;

        // Clear styles if not constraining
        if (!constrainWidth && !constrainHeight) {
            renderAreaRef.current.style.fontSize = "";
            renderAreaRef.current.style.lineHeight = "";
            return;
        }

        const ctx = document.createElement("canvas").getContext("2d");
        if (!ctx) throw new Error("Failed to get canvas context!");

        const rect = containerRef.current.getBoundingClientRect();
        const style = window.getComputedStyle(containerRef.current);
        ctx.font = `${style.fontStyle} ${style.fontVariant} ${style.fontWeight} 1px ${style.fontFamily}`;

        let fontSize = rect.width / ctx.measureText(text).width;
        if (constrainWidth && constrainHeight) {
            fontSize = Math.min(fontSize, rect.height);
        } else if (constrainHeight) {
            fontSize = rect.height;
        }

        renderAreaRef.current.style.fontSize = `${fontSize}px`;
        if (constrainHeight) {
            renderAreaRef.current.style.lineHeight = `${rect.height}px`;
        }
    }, [constrainHeight, constrainWidth, text]);

    useEffect(() => {
        sizeText();

        let obs: ResizeObserver | undefined;
        if (containerRef.current) {
            obs = new ResizeObserver(() => { sizeText(); });
            obs.observe(containerRef.current);
        }
        return () => { obs?.disconnect(); };
    }, [sizeText]);

    return (
        <StyledFitText ref={containerRef} {...restProps}>
            <FitTextRenderArea ref={renderAreaRef}>
                {props.text}
            </FitTextRenderArea>
        </StyledFitText>
    );
};
